import { useCallback, useEffect, useMemo, useState } from 'react';

import SearchResult from '@app/common/cavilha-v2/Algolia';
import ProductHits from '@app/common/cavilha-v2/Algolia/Widgets/ProductHits';
import ProductCard from '@app/common/cavilha-v2/ProductCard';
import parseAlgoliaFilters from '@helpers/datocms/algoliaFilters';
import {
  CONTEXT_PRODUCT_CARD,
  ELEMENT_PRODUCT_CARD_POSITION,
  ROLE_LINK,
  uniqueElementTrack,
} from '@helpers/tracking';
import { formatStringConditions, formatVisualFilters } from '@services/datocms/helpers/taxonomy';
import getAlgoliaFilterData from '@services/datocms/search/getAlgoliaFilterData';
import { ProductListingStorage } from 'contexts/productListingContext';
import type { ListingPageProps } from 'types/listingPageProps';

interface InitialListingFiltersState {
  algoliaFilters: Array<any>;
  visualFilters: object;
  stepFilters: Array<any>;
  stepsOption: Array<any>;
  facetsAttributesToRetrieve: Array<string>;
  isLoadingFilters: boolean;
}

function getInitialListingFiltersState(
  taxonomy: ListingPageProps['taxonomy'],
  facetsConfigurationCategory: ListingPageProps['facetsConfigurationCategory'],
  query: ListingPageProps['query'],
  resultsState: ListingPageProps['resultsState'],
  widgetsCollector: ListingPageProps['widgetsCollector']
): InitialListingFiltersState {
  const needsAsyncFilters = Boolean(query && !taxonomy && !resultsState && !widgetsCollector);

  if (!taxonomy) {
    return {
      algoliaFilters: [],
      visualFilters: {},
      stepFilters: [],
      stepsOption: [],
      facetsAttributesToRetrieve: ['*'],
      isLoadingFilters: needsAsyncFilters,
    };
  }

  const { facetsConfig, visualFilters: visualFilterConfig, ...categoryStepsFilter } = taxonomy;

  return {
    algoliaFilters: facetsConfig ?? [],
    visualFilters: visualFilterConfig ?? {},
    stepFilters: categoryStepsFilter?.stepFilter ?? [],
    stepsOption: categoryStepsFilter ?? [],
    facetsAttributesToRetrieve: facetsConfigurationCategory ?? ['*'],
    isLoadingFilters: false,
  };
}

function ListingPage({
  breadcrumb,
  query,
  pageQuery,
  indexName,
  searchClient,
  allFacets,
  ruleContexts = [],
  taxonomy = null,
  infoBox,
  resultsState,
  widgetsCollector,
  showBreadcrumb = false,
  eventsOrigin = 'PLP',
  initialFilters = '',
  cmsConfig = null,
  type = 'complete',
  skusProducts = [],
  sellerSlug = '',
  title = null,
  tagTitle = 'h1',
  showTitle = true,
  showProductCount = true,
  facetsConfigurationCategory,
  showOrderBy = true,
  isStepBased = false,
  showSideFilters = true,
  userToken,
  hasStructuredData = false,
  filtersFixedMobile,
  isPDPListingPage = false,
  deviceType,
  regionalSearch = false,
}: ListingPageProps): JSX.Element {
  const [listingFiltersState, setListingFiltersState] = useState(() =>
    getInitialListingFiltersState(
      taxonomy,
      facetsConfigurationCategory,
      query,
      resultsState,
      widgetsCollector
    )
  );
  const [toggleFilters, setToggleFilters] = useState<Array<string>>([]);

  const {
    algoliaFilters,
    visualFilters,
    stepFilters,
    stepsOption,
    facetsAttributesToRetrieve,
    isLoadingFilters,
  } = listingFiltersState;

  const listingTitle = useMemo(() => title || query, [title, query]);
  const pageTitle = useMemo(() => (!query ? listingTitle : 'null'), [query, listingTitle]);

  const filters = useMemo(() => {
    if (initialFilters !== '') {
      if (Array.isArray(initialFilters)) {
        return initialFilters.join(' OR ');
      }

      return initialFilters;
    }
    if (taxonomy) {
      return formatStringConditions(taxonomy);
    }

    return '';
  }, [initialFilters, taxonomy]);

  const queryTerm = useMemo(() => {
    if (!query && taxonomy) {
      const { query: queryTaxonomy } = taxonomy;
      if (queryTaxonomy) {
        return queryTaxonomy;
      }

      return undefined;
    }

    return query;
  }, [query, taxonomy]);

  const fetchAlgoliaFiltersFromQuery = useCallback(async () => {
    if (!query) {
      setListingFiltersState(prev => ({ ...prev, isLoadingFilters: false }));
      return;
    }

    const channel =
      typeof window !== 'undefined'
        ? (window as any)?.sessionStorage.getItem('listingChannel') || 'site'
        : 'site';

    if (!resultsState) {
      setListingFiltersState(prev => ({ ...prev, isLoadingFilters: true }));
    }

    const {
      filter: { searchFilters, visualFilterConfig, categoryStepsFilter },
      attributesToRetrieve,
    } = await getAlgoliaFilterData({ query, channel });

    const newVisualFilters = visualFilterConfig ? formatVisualFilters(visualFilterConfig) : {};
    const newStepFilters = categoryStepsFilter ? formatVisualFilters(categoryStepsFilter) : [];
    const newAlgoliaFilters = parseAlgoliaFilters(searchFilters);

    setListingFiltersState({
      algoliaFilters: newAlgoliaFilters,
      visualFilters: newVisualFilters,
      stepFilters: newStepFilters,
      stepsOption: categoryStepsFilter,
      facetsAttributesToRetrieve: attributesToRetrieve,
      isLoadingFilters: false,
    });
  }, [query, resultsState]);

  useEffect(() => {
    if (taxonomy) {
      const taxonomyData = getInitialListingFiltersState(
        taxonomy,
        facetsConfigurationCategory,
        query,
        resultsState,
        widgetsCollector
      );

      setListingFiltersState(taxonomyData);
      return;
    }

    fetchAlgoliaFiltersFromQuery();
  }, [
    taxonomy,
    facetsConfigurationCategory,
    query,
    resultsState,
    widgetsCollector,
    fetchAlgoliaFiltersFromQuery,
  ]);

  const productCardClassName = useMemo(
    () =>
      uniqueElementTrack({
        role: ROLE_LINK,
        context: CONTEXT_PRODUCT_CARD,
        element: ELEMENT_PRODUCT_CARD_POSITION,
      }),
    []
  );

  const productHits = (
    <ProductHits
      hitComponent={ProductCard}
      pageTitle={pageTitle}
      title={listingTitle}
      toggleFilters={toggleFilters}
      eventsOrigin={eventsOrigin}
      cmsConfig={cmsConfig}
      skusProducts={skusProducts}
      sellerSlug={sellerSlug}
      role={ROLE_LINK}
      className={productCardClassName}
      page={taxonomy ? 'category' : undefined}
    />
  );

  const searchResultProps = {
    filtersFixedMobile,
    isStepBased,
    searchClient,
    query: queryTerm,
    pageQuery,
    setToggleFilters,
    breadcrumb,
    showBreadcrumb,
    itemsList: productHits,
    ruleContexts,
    resultsState,
    widgetsCollector,
    facets: allFacets,
    title: listingTitle,
    tagTitle,
    showTitle,
    showProductCount,
    showOrderBy,
    showSideFilters,
    filters,
    eventsOrigin,
    type,
    algoliaFilters,
    visualFilters,
    stepFilters,
    stepsOption,
    infoBox,
    attributesToRetrieve: facetsAttributesToRetrieve,
    userToken,
    hasStructuredData,
    isLoadingFilters,
    isPDPListingPage,
    regionalSearch,
  };

  return (
    <ProductListingStorage initialIndex={indexName} serverDeviceType={deviceType}>
      <SearchResult {...searchResultProps} />
    </ProductListingStorage>
  );
}

export default ListingPage;
